/**
 * Utilidades para manejo de tokens CSRF.
 * Centraliza la lógica de CSRF para evitar duplicación.
 */

// Almacenar token CSRF en memoria (no en localStorage por seguridad)
let csrfToken: string | null = null;

/**
 * Métodos HTTP que requieren token CSRF (métodos mutables).
 */
const MUTABLE_METHODS = ['post', 'put', 'delete', 'patch'] as const;

/**
 * Verifica si un método HTTP es mutable (requiere CSRF).
 * @param method - Método HTTP a verificar
 * @returns true si el método es mutable, false en caso contrario
 */
export const isMutableMethod = (method: string | undefined): boolean => {
  if (!method) return false;
  return MUTABLE_METHODS.includes(method.toLowerCase() as typeof MUTABLE_METHODS[number]);
};

/**
 * Obtiene el token CSRF actual.
 * @returns Token CSRF o null si no está disponible
 */
export const getCsrfToken = (): string | null => {
  return csrfToken;
};

/**
 * Establece el token CSRF.
 * @param token - Token CSRF a establecer
 */
export const setCsrfToken = (token: string | null): void => {
  csrfToken = token;
};

/**
 * Limpia el token CSRF.
 */
export const clearCsrfToken = (): void => {
  csrfToken = null;
};

/**
 * Agrega el token CSRF a los headers de una configuración de request si el método es mutable.
 * @param headers - Headers del request (puede ser undefined)
 * @param method - Método HTTP del request
 */
export const addCsrfTokenToHeaders = (
  headers: Record<string, any> | undefined,
  method: string | undefined
): void => {
  if (headers && isMutableMethod(method) && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
};

/**
 * Actualiza el token CSRF desde los headers de una respuesta.
 * @param responseHeaders - Headers de la respuesta
 */
export const updateCsrfTokenFromHeaders = (
  responseHeaders: Record<string, any>
): void => {
  const newCsrfToken = responseHeaders['x-csrf-token'];
  if (newCsrfToken) {
    csrfToken = newCsrfToken;
  }
};

